import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOne, useList } from "@refinedev/core";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/forms/PageHeader";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";
import { MoneyInput } from "@/components/forms/MoneyInput";
import { FileUpload } from "@/components/forms/FileUpload";

/**
 * Wizard de anulación de 3 pasos.
 * - Paso 1: motivo + confirmar venta
 * - Paso 2: devolución esperada de la financiera
 * - Paso 3: gastos por cancelación (que se descuentan del próximo corte de la tienda)
 */
export function AnularVentaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [motivo, setMotivo] = useState("");
  const [montoEsperado, setMontoEsperado] = useState(0);
  const [yaRecibido, setYaRecibido] = useState(false);
  const [montoRecibido, setMontoRecibido] = useState(0);
  const [fechaRecibido, setFechaRecibido] = useState("");
  const [referenciaDev, setReferenciaDev] = useState("");
  const [montoGastos, setMontoGastos] = useState(0);
  const [detalleGastos, setDetalleGastos] = useState("");
  const [soporte, setSoporte] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: venta } = useOne({ resource: "ventas", id });
  const { data: pagos } = useList({
    resource: "venta_pagos",
    filters: id ? [{ field: "venta_id", operator: "eq", value: id }] : [],
    meta: { select: "*, financieras(nombre)" },
  });

  if (!venta?.data) return <div className="card p-8 text-center">Cargando...</div>;
  const v = venta.data;

  if (v.estado === "anulada") {
    return (
      <div className="card p-6 text-center">
        <p className="text-error font-medium">Esta venta ya está anulada.</p>
      </div>
    );
  }

  async function confirmar() {
    if (!user || !id) return;
    setSubmitting(true);
    try {
      // Subir soporte si hay
      let soportePath: string | null = null;
      if (soporte) {
        const ext = soporte.name.split(".").pop();
        soportePath = `anulaciones/${id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("documentos")
          .upload(soportePath, soporte);
        if (upErr) throw upErr;
      }

      const estado = yaRecibido
        ? "devolucion_recibida"
        : montoEsperado > 0
          ? "devolucion_pendiente"
          : "registrada";

      const { error: anulErr } = await supabase.from("anulaciones").insert({
        venta_id: id,
        periodo_origen_id: v.periodo_cobro_id,
        motivo,
        estado,
        monto_devolucion_esperado: montoEsperado,
        monto_devolucion_recibido: yaRecibido ? montoRecibido : 0,
        fecha_devolucion_recibida: yaRecibido ? fechaRecibido : null,
        referencia_devolucion: yaRecibido ? referenciaDev : null,
        monto_gastos_cancelacion: montoGastos,
        detalle_gastos: detalleGastos || null,
        documento_soporte_url: soportePath,
        registrado_por: user.id,
      });
      if (anulErr) throw anulErr;

      // Marcar venta como anulada
      const { error: vErr } = await supabase
        .from("ventas")
        .update({ estado: "anulada", motivo_anulacion: motivo })
        .eq("id", id);
      if (vErr) throw vErr;

      toast.success("Venta anulada");
      navigate("/anulaciones");
    } catch (err) {
      toast.error("Error", { description: (err as Error).message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-xl space-y-4">
      <header className="flex items-center justify-between">
        <button
          onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))}
          className="flex items-center gap-1 text-fg-secondary"
        >
          <ArrowLeft className="size-4" /> Atrás
        </button>
        <span className="text-sm font-medium text-fg-secondary">{step}/3</span>
      </header>

      <PageHeader
        title={`Anular venta #${v.numero_consecutivo}`}
        subtitle={`${v.cliente_nombre} · ${v.producto_marca} ${v.producto_modelo}`}
      />

      {step === 1 && (
        <div className="space-y-4">
          <section className="card p-3 text-sm">
            <p className="font-medium mb-2">Pagos asociados:</p>
            <ul className="space-y-1">
              {pagos?.data.map((p) => (
                <li key={p.id as string} className="flex justify-between">
                  <span>
                    {p.tipo === "efectivo"
                      ? "Efectivo"
                      : (p.financieras as { nombre: string })?.nombre}
                  </span>
                  <MoneyDisplay value={p.monto as number} />
                </li>
              ))}
            </ul>
          </section>
          <div className="space-y-1">
            <label className="text-sm font-medium">Motivo de la anulación *</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="input min-h-24"
              placeholder="Cliente se arrepintió, defecto del producto..."
            />
          </div>
          <button
            onClick={() => setStep(2)}
            disabled={motivo.trim().length < 3}
            className="btn-primary w-full"
          >
            Siguiente
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Devolución del aliado</h3>
          <p className="text-sm text-fg-muted">
            Si esta venta tenía pagos por financiera, la financiera debe devolver el dinero al central.
          </p>
          <div className="space-y-1">
            <label className="text-sm font-medium">Monto esperado a devolver</label>
            <MoneyInput value={montoEsperado} onChange={setMontoEsperado} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={yaRecibido}
              onChange={(e) => setYaRecibido(e.target.checked)}
              className="size-4"
            />
            Ya recibimos la devolución
          </label>
          {yaRecibido && (
            <div className="space-y-3 pl-6 border-l-2 border-border">
              <div className="space-y-1">
                <label className="text-sm font-medium">Fecha recibida</label>
                <input
                  type="date"
                  value={fechaRecibido}
                  onChange={(e) => setFechaRecibido(e.target.value)}
                  className="input"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Monto recibido</label>
                <MoneyInput value={montoRecibido} onChange={setMontoRecibido} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Referencia bancaria</label>
                <input
                  value={referenciaDev}
                  onChange={(e) => setReferenciaDev(e.target.value)}
                  className="input"
                />
              </div>
            </div>
          )}
          <button onClick={() => setStep(3)} className="btn-primary w-full">
            Siguiente
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Gastos por cancelación</h3>
          <p className="text-sm text-fg-muted">
            Estos gastos se descuentan del próximo corte de la tienda como ajuste.
          </p>
          <div className="space-y-1">
            <label className="text-sm font-medium">Monto del gasto</label>
            <MoneyInput value={montoGastos} onChange={setMontoGastos} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Detalle (opcional)</label>
            <textarea
              value={detalleGastos}
              onChange={(e) => setDetalleGastos(e.target.value)}
              className="input min-h-20"
              placeholder="Penalidad de la financiera, costos administrativos..."
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Documento soporte (opcional)</label>
            <FileUpload value={soporte} onChange={setSoporte} hint="PDF o imagen" />
          </div>
          <button onClick={confirmar} disabled={submitting} className="btn-primary w-full">
            {submitting ? "Anulando..." : "Confirmar anulación"}
          </button>
        </div>
      )}
    </div>
  );
}
